import { useMyLocks, useReleaseMyLocks, useHeldLocksGuard } from '@/shared/locks/useMyLocks';

/**
 * Shell-level advisory strip: "You hold N inventory locks" + Release all (BigList #18).
 *
 * <p><b>Why this exists.</b> PB gives an operator holding a lock no signal whatsoever. They find
 * out when a colleague hits `This Location is locked by Mover <RF0007       >.`, and the only cure
 * is an administrator opening Lock Maintenance (`w_lock`) and deleting the row by hand
 * (`docs/audits/biglist/18-inventory-locking.md` §1.4). The banner is the operator-facing half of
 * closing that: it tells them while they still have a session in which to fix it. The sweeper
 * (ADR-0044) is the backstop for when they do not.
 *
 * <p><b>Renders nothing when the session holds no locks</b> — which is almost always. It must be
 * invisible in the normal case or it becomes chrome operators learn to ignore, and it must not
 * reserve layout height while loading, hence the early return rather than a skeleton.
 *
 * <p>Amber, not red: holding a lock is normal and expected while working a screen. The banner is
 * information ("this is outstanding"), not an error.
 */
export function HeldLocksBanner() {
  const { data } = useMyLocks();
  const release = useReleaseMyLocks();
  const count = data?.count ?? 0;
  // Native close prompt registered only while locks are actually held.
  useHeldLocksGuard(count);

  if (count <= 0) return null;

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-50 px-4 py-1.5 text-[13px] text-amber-900"
    >
      <span className="font-medium">
        You hold {count} inventory {count === 1 ? 'lock' : 'locks'}
      </span>
      <span className="text-amber-800">{summarize(data?.items ?? [])}</span>
      <button
        type="button"
        onClick={() => release.mutate()}
        disabled={release.isPending}
        className="ml-auto rounded border border-amber-400 bg-white px-2 py-0.5 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
      >
        {release.isPending ? 'Releasing…' : 'Release all'}
      </button>
    </div>
  );
}

/**
 * "SP / R-01 (location), SP / RTN-03 (station)" — enough for the operator to recognise the
 * location without opening a screen. Capped, because a location lock at a busy slot is one lock
 * here but the operator could legitimately hold several.
 */
function summarize(items: { kind: string; warehouseId: string; id: string }[]): string {
  const shown = items
    .slice(0, 4)
    .map((i) => `${i.warehouseId} / ${i.id} (${i.kind})`)
    .join(', ');
  return items.length > 4 ? `${shown}, +${items.length - 4} more` : shown;
}
